import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { compressPhoto } from '../utils/imageCompress';
import { GALLERY_LIMITS, ITEM_KINDS } from '../constants/gallery';
import { addGalleryItem } from '../firebase/galleryService';
import { shareProjectForPublish } from '../firebase/workspaceService';
import './PublishDesignModal.css';

// ── PUBLISH A 3D DESIGN ──────────────────────────────────────────────────────
// Opened from a project's "..." menu in the workspace. Publishing needs a cover
// image and a short description; the resulting item lands in the same gallery
// the Dashboard's "3D" tab manages, carrying `projectId` so the published card
// stays linked to the project it came from.
//
// The cover is uploaded rather than auto-captured: a canvas capture would mean
// mounting the 3D scene here just to screenshot it. The user picks the render
// they actually want to show, which is usually a better frame anyway.
export default function PublishDesignModal({ project, onClose, onPublished }) {
  const { user } = useAuth();
  const { t } = useT();
  const fileRef = useRef(null);

  const [cover, setCover] = useState(null);       // compressPhoto() output
  const [preview, setPreview] = useState('');
  const [description, setDescription] = useState('');
  // Off by default — see the note where they are saved.
  const [allowCopy, setAllowCopy] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const image = await compressPhoto(file);
      setCover(image);
      setPreview(image.thumbUrl);
    } catch (err) {
      setError(t(err?.message === 'too-large' ? 'gal_err_large' : 'gal_err_upload'));
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!cover) { setError(t('pub_err_cover')); return; }
    setBusy(true);
    setError('');
    try {
      await addGalleryItem(ITEM_KINDS.DESIGN, user.uid, cover, {
        title: project.projectName,
        description,
        projectId: project.id,
        visible: true,
        // ── WHAT VIEWERS MAY DO WITH IT ────────────────────────────────────
        // Both default to OFF, and that is the point: publishing shows the
        // work, it does not hand it over. Whoever made the design decides
        // whether a stranger may take a copy of it into their own workspace or
        // save a picture of it, and the viewer only offers the buttons the
        // author actually granted (see SharedProject).
        allowCopy,
        allowDownload,
      });
      // Open the project for public READING, so the card's link resolves to a
      // view-only 3D scene instead of a permission error. Writes stay
      // owner-only — /share mounts the editor with readOnly.
      await shareProjectForPublish(project.id, { allowCopy, allowDownload });
      onPublished?.();
      onClose();
    } catch (err) {
      setError(err?.message || t('gal_err_save'));
      setBusy(false);
    }
  }

  return (
    <div className="pdm-overlay" onClick={() => !busy && onClose()}>
      <div className="pdm" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="pdm-title">{t('pub_title')}</h3>
        <p className="pdm-sub">{t('pub_sub')}</p>

        {error && (
          <div className="af-toast af-toast--error" role="alert">
            <span className="af-toast__icon">⚠</span>{error}
          </div>
        )}

        <button
          type="button"
          className="pdm-cover"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
        >
          {preview
            ? <img src={preview} alt="" />
            : <span className="pdm-cover-ph">{busy ? '…' : t('pub_pick_cover')}</span>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} disabled={busy} />

        <label className="pdm-field">
          <span className="pdm-label">
            {t('gal_desc_ph')}
            <span className="pdm-count">{description.length}/{GALLERY_LIMITS.description}</span>
          </span>
          <textarea
            className="pdm-textarea"
            rows={3}
            value={description}
            maxLength={GALLERY_LIMITS.description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('gal_desc_ph')}
            disabled={busy}
          />
        </label>

        {/* What a viewer is allowed to do with the published design. */}
        <div className="pdm-perms">
          <label className="pdm-check">
            <input
              type="checkbox"
              checked={allowCopy}
              onChange={e => setAllowCopy(e.target.checked)}
              disabled={busy}
            />
            <span>{t('pub_allow_copy')}</span>
          </label>
          <label className="pdm-check">
            <input
              type="checkbox"
              checked={allowDownload}
              onChange={e => setAllowDownload(e.target.checked)}
              disabled={busy}
            />
            <span>{t('pub_allow_download')}</span>
          </label>
        </div>

        <div className="pdm-actions">
          <button type="button" className="pdm-btn" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </button>
          <button type="button" className="pdm-btn pdm-btn--primary" onClick={handlePublish} disabled={busy || !cover}>
            {busy && <span className="af-spinner" />}
            {t('pub_publish')}
          </button>
        </div>
      </div>
    </div>
  );
}
